import React, { useState, useEffect } from 'react';
import { FileText, Linkedin, MessageSquare, BookOpen, GraduationCap } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

const Cover = () => {
    // Define all services with their content
    const services = [
        {
            id: 'resume',
            icon: <FileText size={24} className="service-icon" />,
            title: "Resume Builder",
            heading: "Create a job-winning resume through customizable templates, successful resume strategies, and in-depth feedback",
            features: [
                "ATS-friendly resume formats that get you hired",
                "Strategies to tailor your resume to each job and highlight key achievements",
                "Personalized feedback from resume experts and industry mentors"
            ],
            image: "../../../images/Resume/ResumeBuild.png"
        },
        {
            id: 'linkedin',
            icon: <Linkedin size={24} className="service-icon" />,
            title: "Video Interview",
            heading: "Ace your interviews with AI-powered video analysis, expert insights, and confidence-boosting feedback",
            features: [
                "Record or upload your mock interview video with ease",
                "AI-powered analysis to assess eye contact, body language, speech clarity, confidence and so on...",
                "Detailed feedback on filler words, pacing, and overall impression to help you improve"
            ],
            image: "../../../images/Resume/videointer.png"
        },
        {
            id: 'interviews',
            icon: <MessageSquare size={24} className="service-icon" />,
            title: "Personalized Mentor",
            heading: "Get one-on-one guidance from experienced mentors to accelerate your growth and career success",
            features: [
                "Explore detailed mentor profiles to find the perfect match for your goals",
                "Easily schedule one-on-one video call sessions at your convenience",
                "Receive personalized feedback, career advice, and interview tips directly from mentors"
            ],
            image: "../../../images/Resume/mentorassign.png"
        },
        {
            id: 'job-search',
            icon: <BookOpen size={24} className="service-icon" />,
            title: "Job Search Support",
            heading: "Develop a strategic job search plan that targets the right opportunities for your career goals",
            features: [
                "Proven job search strategies tailored to your industry and experience level",
                "Access to exclusive job opportunities through our network",
                "Guidance on following up and managing your application pipeline"
            ],
            image: "../../../images/404/jobSearch.jpg"
        },
        {
            id: 'negotiation',
            icon: <GraduationCap size={24} className="service-icon" />,
            title: "Career Path Navigator",
            heading: "Follow a structured, 8-level journey designed to build your skills, boost your profile, and unlock career opportunities",
            features: [
                "Progress through 8 curated levels tailored for career excellence and clarity",
                "Each level includes 6 essential modules: Core Skills, Professional Courses, Must-Read Books, Key Events, LinkedIn Growth, and Beyond",
                "Track your growth with completion badges and milestone achievements at every level"
            ],
            image: "../../../images/about/milestone-8.png"
        }
    ];

    // Set default active service (resume)
    const [activeService, setActiveService] = useState(services[0]);
    const [animate, setAnimate] = useState(false);
    const [showServices, setShowServices] = useState(true);

    // Handle service box click with animation
    const handleServiceClick = (service) => {
        setAnimate(false);
        
        // On mobile, when clicking a service, hide the service list and show the content
        if (window.innerWidth < 768) {
            setShowServices(false);
        }
        
        setTimeout(() => {
            setActiveService(service);
            setAnimate(true);
        }, 300);
    };

    // Return to service list on mobile
    const handleBackToServices = () => {
        setShowServices(true);
    };

    // Set initial animation
    useEffect(() => {
        setAnimate(true);
        
        // Reset view on window resize
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setShowServices(true);
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <>
            <div className="cover-container">
                <div className="container">
                    <h2 className="text-center fw-bold mb-4 mb-md-5 section-title">What We Cover</h2>

                    <div className="row g-4">
                        {/* Mobile Back Button */}
                        {!showServices && (
                            <div className="col-12 d-md-none mb-3">
                                <button 
                                    className="btn btn-sm back-button"
                                    onClick={handleBackToServices}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
                                    </svg>
                                    Back to Services
                                </button>
                            </div>
                        )}
                        
                        {/* Left column with service boxes */}
                        <div className={`col-md-5 ${showServices ? 'd-block' : 'd-none d-md-block'}`}>
                            <div className="service-list">
                                {services.map((service) => (
                                    <ServiceBox
                                        key={service.id}
                                        icon={service.icon}
                                        title={service.title}
                                        active={activeService.id === service.id}
                                        onClick={() => handleServiceClick(service)}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Right column with features and preview */}
                        <div className={`col-md-7 ${showServices ? 'd-none d-md-block' : 'd-block'}`}>
                            <div className={`service-content ${animate ? 'fade-in' : 'fade-out'}`}>
                                <h4 className="service-heading mb-3 mb-md-4">{activeService.heading}</h4>

                                <div className="features-container mb-4">
                                    {activeService.features.map((feature, index) => (
                                        <FeatureItem key={index} text={feature} />
                                    ))}
                                </div>

                                <div className="preview-box">
                                    <img
                                        src={activeService.image}
                                        alt={activeService.title}
                                        className="img-fluid rounded shadow"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add a subtle decorative element */}
                <div className="decoration-circle decoration-circle-1"></div>
                <div className="decoration-circle decoration-circle-2"></div>
            </div>
            <style>
                {`
                    /* cover.css */
                    :root {
                        --primary-purple: #6600CC;
                        --primary-green: #006600;
                        --primary-light-green: rgb(227, 252, 227);
                        --light-purple: rgba(102, 0, 204, 0.1);
                        --medium-purple: rgba(102, 0, 204, 0.2);
                        --light-green: rgba(0, 102, 0, 0.1);
                        --text-dark: #333333;
                        --text-medium: #666666;
                        --transition-speed: 0.3s;
                    }

                    .cover-container {
                        position: relative;
                        overflow: hidden;
                        background-color: #ffffff;
                        padding: 3rem 0;
                    }

                    .section-title {
                        color: var(--text-dark);
                        font-size: 2.25rem;
                        position: relative;
                        display: inline-block;
                        margin: 0 auto 2.5rem;
                    }

                    .section-title::after {
                        content: '';
                        position: absolute;
                        width: 60px;
                        height: 4px;
                        background: linear-gradient(to right, var(--primary-purple), var(--primary-green));
                        bottom: -10px;
                        left: 50%;
                        transform: translateX(-50%);
                        border-radius: 2px;
                    }

                    /* Service Box Styling */
                    .service-list {
                        display: flex;
                        flex-direction: column;
                        gap: 16px;
                    }

                    .service-box {
                        display: flex;
                        align-items: center;
                        padding: 16px;
                        border-radius: 10px;
                        background-color: white;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                        cursor: pointer;
                        transition: all var(--transition-speed) ease;
                        position: relative;
                        border-left: 4px solid transparent;
                    }

                    .service-box:hover {
                        transform: translateX(5px);
                        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
                    }

                    .service-box.active {
                        border-left: 4px solid var(--primary-green);
                        background-color: var(--primary-light-green);
                    }

                    .service-icon-wrapper {
                        width: 42px;
                        height: 42px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 50%;
                        background-color: var(--light-purple);
                        margin-right: 16px;
                        transition: all var(--transition-speed) ease;
                        flex-shrink: 0;
                    }

                    .service-box.active .service-icon-wrapper {
                        background-color: var(--medium-purple);
                    }

                    .service-icon {
                        color: var(--primary-purple);
                    }

                    .service-title {
                        font-size: 1.05rem;
                        font-weight: 600;
                        color: var(--text-dark);
                    }

                    .active-indicator {
                        position: absolute;
                        right: 16px;
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        background-color: var(--primary-purple);
                    }

                    /* Service Content Styling */
                    .service-content {
                        padding: 10px;
                        transition: opacity var(--transition-speed) ease;
                    }

                    .service-heading {
                        color: var(--text-dark);
                        font-weight: 600;
                        line-height: 1.4;
                        margin-bottom: 20px;
                        font-size: 1.25rem;
                    }

                    .fade-in {
                        opacity: 1;
                    }

                    .fade-out {
                        opacity: 0;
                    }

                    /* Feature Item Styling */
                    .features-container {
                        margin-bottom: 24px;
                    }

                    .feature-item {
                        display: flex;
                        align-items: flex-start;
                        margin-bottom: 14px;
                    }

                    .feature-check {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background-color: var(--primary-green);
                        color: white;
                        margin-right: 12px;
                        flex-shrink: 0;
                    }

                    .feature-text {
                        font-size: 0.95rem;
                        line-height: 1.5;
                        color: var(--text-medium);
                    }

                    /* Preview Box Styling */
                    .preview-box {
                        border-radius: 10px;
                        overflow: hidden;
                        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                        transition: transform var(--transition-speed) ease;
                    }

                    .preview-box:hover {
                        transform: translateY(-5px);
                    }

                    .preview-box img {
                        width: 100%;
                        display: block;
                        transition: transform 0.5s ease;
                    }

                    .preview-box:hover img {
                        transform: scale(1.02);
                    }

                    /* Back Button Styling */
                    .back-button {
                        background-color: var(--light-purple);
                        color: var(--primary-purple);
                        border: none;
                        border-radius: 8px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 8px 16px;
                        font-weight: 500;
                        transition: all 0.2s ease;
                    }

                    .back-button:hover {
                        background-color: var(--medium-purple);
                    }

                    /* Decorative elements */
                    .decoration-circle {
                        position: absolute;
                        border-radius: 50%;
                        z-index: -1;
                        opacity: 0.3;
                    }

                    .decoration-circle-1 {
                        background-color: var(--light-purple);
                        width: 300px;
                        height: 300px;
                        top: -100px;
                        right: -100px;
                    }

                    .decoration-circle-2 {
                        background-color: var(--light-green);
                        width: 200px;
                        height: 200px;
                        bottom: -50px;
                        left: -50px;
                    }

                    /* Responsive adjustments */
                    @media (max-width: 992px) {
                        .section-title {
                            font-size: 2rem;
                            margin-bottom: 2rem;
                        }
                        
                        .service-list {
                            margin-bottom: 30px;
                        }
                        
                        .service-box {
                            padding: 14px;
                        }
                        
                        .service-icon-wrapper {
                            width: 38px;
                            height: 38px;
                        }
                        
                        .service-title {
                            font-size: 1rem;
                        }
                    }

                    @media (max-width: 768px) {
                        .cover-container {
                            padding: 2rem 0;
                        }
                        
                        .section-title {
                            font-size: 1.8rem;
                        }
                        
                        .service-heading {
                            font-size: 1.15rem;
                            margin-bottom: 16px;
                        }
                        
                        .feature-item {
                            margin-bottom: 60px;
                        }
                        
                        .feature-check {
                            width: 22px;
                            height: 22px;
                        }
                        
                        .feature-text {
                            font-size: 0.9rem;
                        }
                        
                        .decoration-circle-1,
                        .decoration-circle-2 {
                            opacity: 0.2;
                            width: 150px;
                            height: 150px;
                        }
                    }

                    @media (max-width: 576px) {
                        .section-title {
                            font-size: 1.6rem;
                        }
                        
                        .service-heading {
                            font-size: 1.1rem;
                        }
                        
                        .decoration-circle-1,
                        .decoration-circle-2 {
                            display: none;
                        }
                        
                        .service-box {
                            padding: 12px;
                        }
                        
                        .service-icon-wrapper {
                            width: 36px;
                            height: 36px;
                        }
                        
                        .service-title {
                            font-size: 0.95rem;
                        }
                    }
                `}
            </style>
        </>
    );
};

// Service Box Component
const ServiceBox = ({ icon, title, active, onClick }) => {
    return (
        <div
            className={`service-box ${active ? 'active' : ''}`}
            onClick={onClick}
        >
            <div className="service-icon-wrapper">
                {icon}
            </div>
            <div className="service-title">{title}</div>
            {active && <div className="active-indicator"></div>}
        </div>
    );
};

// Feature Item Component
const FeatureItem = ({ text }) => {
    return (
        <div className="feature-item">
            <div className="feature-check">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
            </div>
            <div className="feature-text">{text}</div>
        </div>
    );
};

export default Cover;